import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, Output, NgModule, Inject, HostListener } from '@angular/core';
import { tuiRgbToHex, tuiHexToRgb, TuiDestroyService, tuiRgbToHsv, tuiHsvToRgb, tuiRound, tuiPure, tuiDefaultSort, tuiParseColor, TuiActiveZoneModule } from '@taiga-ui/cdk';
import * as i1 from '@taiga-ui/kit';
import { TuiValueAccessorModule, TuiSelectModule, TuiInputCountModule } from '@taiga-ui/kit';
import * as i2 from '@taiga-ui/core';
import { TuiPrimitiveTextfieldModule, TuiTextfieldControllerModule, TuiDropdownModule, TuiDataListModule, TuiHintModule, TuiButtonModule, TuiHostedDropdownModule, TuiSvgModule, TuiGroupModule } from '@taiga-ui/core';
import * as i3 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@maskito/angular';
import { MaskitoModule } from '@maskito/angular';
import { __decorate } from 'tslib';
import * as i3$1 from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { TuiPickerService } from '@taiga-ui/addon-editor/services';
import * as i1$2 from 'rxjs';
import { TUI_EDITOR_COLOR_SELECTOR_MODE_NAMES } from '@taiga-ui/addon-editor/tokens';
import { tuiParseGradient, tuiGetGradientData } from '@taiga-ui/addon-editor/utils';

const HEX_MODE_LENGTH = 6;
class TuiColorEditComponent {
    constructor() {
        this.color = [0, 0, 0, 1];
        this.colorChange = new EventEmitter();
        this.hexMask = {
            mask: new RegExp(`^[A-F\\d]{0,${HEX_MODE_LENGTH}}$`, 'gi'),
        };
        this.modes = ['HEX', 'RGB'];
        this.mode = this.modes[0];
    }
    get isHex() {
        return this.mode === this.modes[0];
    }
    get hex() {
        return tuiRgbToHex(this.color[0], this.color[1], this.color[2]).replace('#', '');
    }
    get opacity() {
        return Math.round(this.color[3] * 100);
    }
    onHexChange(hex) {
        if (hex.length !== HEX_MODE_LENGTH) {
            return;
        }
        const rgb = tuiHexToRgb(hex);
        this.updateColor([rgb[0], rgb[1], rgb[2], this.color[3]]);
    }
    onRgbChange(...rgba) {
        this.updateColor(rgba);
    }
    updateColor(color) {
        this.color = color;
        this.colorChange.emit(color);
    }
}
TuiColorEditComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorEditComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TuiColorEditComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiColorEditComponent, selector: "tui-color-edit", inputs: { color: "color" }, outputs: { colorChange: "colorChange" }, ngImport: i0, template: "<tui-select\n    tuiTextfieldSize=\"m\"\n    tuiDropdownAlign=\"left\"\n    tuiDropdownLimitWidth=\"min\"\n    class=\"t-select tui-group__auto-width-item\"\n    [tuiTextfieldLabelOutside]=\"true\"\n    [(ngModel)]=\"mode\"\n>\n    <tui-data-list\n        *tuiDataList\n        size=\"s\"\n    >\n        <button\n            *ngFor=\"let item of modes\"\n            tuiOption\n            [value]=\"item\"\n        >\n            {{ item }}\n        </button>\n    </tui-data-list>\n</tui-select>\n<tui-primitive-textfield\n    *ngIf=\"isHex; else rgb\"\n    tuiValueAccessor\n    tuiTextfieldSize=\"m\"\n    [maskito]=\"hexMask\"\n    [tuiTextfieldLabelOutside]=\"true\"\n    [value]=\"hex\"\n    (valueChange)=\"onHexChange($event)\"\n></tui-primitive-textfield>\n<ng-template #rgb>\n    <tui-input-count\n        tuiTextfieldSize=\"m\"\n        [tuiTextfieldLabelOutside]=\"true\"\n        [hideButtons]=\"true\"\n        [max]=\"255\"\n        [ngModel]=\"color[0]\"\n        (ngModelChange)=\"onRgbChange($event, color[1], color[2], color[3])\"\n    ></tui-input-count>\n    <tui-input-count\n        tuiTextfieldSize=\"m\"\n        [tuiTextfieldLabelOutside]=\"true\"\n        [hideButtons]=\"true\"\n        [max]=\"255\"\n        [ngModel]=\"color[1]\"\n        (ngModelChange)=\"onRgbChange(color[0], $event, color[2], color[3])\"\n    ></tui-input-count>\n    <tui-input-count\n        tuiTextfieldSize=\"m\"\n        [tuiTextfieldLabelOutside]=\"true\"\n        [hideButtons]=\"true\"\n        [max]=\"255\"\n        [ngModel]=\"color[2]\"\n        (ngModelChange)=\"onRgbChange(color[0], color[1], $event, color[3])\"\n    ></tui-input-count>\n</ng-template>\n<tui-input-count\n    tuiTextfieldPostfix=\"%\"\n    tuiTextfieldSize=\"m\"\n    class=\"t-opacity tui-group__auto-width-item\"\n    [tuiTextfieldLabelOutside]=\"true\"\n    [hideButtons]=\"true\"\n    [max]=\"100\"\n    [ngModel]=\"opacity\"\n    (ngModelChange)=\"onRgbChange(color[0], color[1], color[2], $event / 100)\"\n></tui-input-count>\n", styles: [":host{display:flex}.t-select{width:4.875rem}.t-opacity{width:4.275rem}\n"], components: [{ type: i1.TuiSelectComponent, selector: "tui-select", inputs: ["stringify", "identityMatcher", "valueContent"] }, { type: i2.TuiDataListComponent, selector: "tui-data-list", inputs: ["role", "emptyContent", "size"] }, { type: i2.TuiOptionComponent, selector: "button[tuiOption], a[tuiOption]", inputs: ["size", "role", "disabled", "value"] }, { type: i2.TuiPrimitiveTextfieldComponent, selector: "tui-primitive-textfield", inputs: ["editable", "filler", "iconCleaner", "readOnly", "invalid", "disabled", "prefix", "postfix", "value"], outputs: ["valueChange"] }, { type: i1.TuiInputCountComponent, selector: "tui-input-count", inputs: ["step", "min", "max", "hideButtons", "prefix", "postfix"] }], directives: [{ type: i1.TuiSelectDirective, selector: "tui-select" }, { type: i2.TuiTextfieldSizeDirective, selector: "[tuiTextfieldSize]", inputs: ["tuiTextfieldSize"] }, { type: i2.TuiDropdownOptionsDirective, selector: "[tuiDropdownAlign], [tuiDropdownAppearance], [tuiDropdownDirection], [tuiDropdownLimitWidth], [tuiDropdownMinHeight], [tuiDropdownMaxHeight], [tuiDropdownOffset]", inputs: ["tuiDropdownAlign", "tuiDropdownAppearance", "tuiDropdownDirection", "tuiDropdownLimitWidth", "tuiDropdownMinHeight", "tuiDropdownMaxHeight", "tuiDropdownOffset"] }, { type: i2.TuiTextfieldLabelOutsideDirective, selector: "[tuiTextfieldLabelOutside]", inputs: ["tuiTextfieldLabelOutside"] }, { type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i2.TuiDataListDirective, selector: "ng-template[tuiDataList]" }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.TuiPrimitiveTextfieldDirective, selector: "tui-primitive-textfield" }, { type: i1.TuiValueAccessorDirective, selector: "[tuiValueAccessor]" }, { type: i5.MaskitoDirective, selector: "[maskito]", inputs: ["maskito", "maskitoElement"] }, { type: i1.TuiInputCountDirective, selector: "tui-input-count" }, { type: i2.TuiTextfieldPostfixDirective, selector: "[tuiTextfieldPostfix]", inputs: ["tuiTextfieldPostfix"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorEditComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-color-edit',
                    templateUrl: './color-edit.template.html',
                    styleUrls: ['./color-edit.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { color: [{
                type: Input
            }], colorChange: [{
                type: Output
            }] } });

class TuiColorEditModule {
}
TuiColorEditModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorEditModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiColorEditModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorEditModule, declarations: [TuiColorEditComponent], imports: [CommonModule,
        FormsModule,
        MaskitoModule,
        TuiValueAccessorModule,
        TuiSelectModule,
        TuiPrimitiveTextfieldModule,
        TuiTextfieldControllerModule,
        TuiDropdownModule,
        TuiInputCountModule,
        TuiDataListModule], exports: [TuiColorEditComponent] });
TuiColorEditModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorEditModule, imports: [[
            CommonModule,
            FormsModule,
            MaskitoModule,
            TuiValueAccessorModule,
            TuiSelectModule,
            TuiPrimitiveTextfieldModule,
            TuiTextfieldControllerModule,
            TuiDropdownModule,
            TuiInputCountModule,
            TuiDataListModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorEditModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        MaskitoModule,
                        TuiValueAccessorModule,
                        TuiSelectModule,
                        TuiPrimitiveTextfieldModule,
                        TuiTextfieldControllerModule,
                        TuiDropdownModule,
                        TuiInputCountModule,
                        TuiDataListModule,
                    ],
                    declarations: [TuiColorEditComponent],
                    exports: [TuiColorEditComponent],
                }]
        }] });

class TuiFlatPickerComponent {
    constructor(point$) {
        this.value = [0, 0];
        this.valueChange = new EventEmitter();
        point$.subscribe(point => {
            this.value = point;
            this.valueChange.emit([point[0], point[1]]);
        });
    }
    get left() {
        return this.value[0] * 100;
    }
    get top() {
        return this.value[1] * 100;
    }
}
TuiFlatPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFlatPickerComponent, deps: [{ token: TuiPickerService }], target: i0.ɵɵFactoryTarget.Component });
TuiFlatPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiFlatPickerComponent, selector: "tui-flat-picker", inputs: { value: "value" }, outputs: { valueChange: "valueChange" }, providers: [TuiDestroyService, TuiPickerService], ngImport: i0, template: "<div\n    class=\"t-circle\"\n    [style.left.%]=\"left\"\n    [style.top.%]=\"top\"\n></div>\n", styles: [":host{position:relative;display:block}.t-circle{position:absolute;width:1rem;height:1rem;border-radius:100%;margin:-.5rem 0 0 -.5rem;box-shadow:inset 0 0 0 1px var(--tui-base-01),0 0 0 1px var(--tui-base-03)}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFlatPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-flat-picker',
                    templateUrl: './flat-picker.template.html',
                    styleUrls: ['./flat-picker.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [TuiDestroyService, TuiPickerService],
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Observable, decorators: [{
                    type: Inject,
                    args: [TuiPickerService]
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class TuiLinearPickerComponent {
    constructor(point$) {
        this.value = 0;
        this.valueChange = new EventEmitter();
        point$.subscribe(([x]) => {
            this.value = x;
            this.valueChange.emit(x);
        });
    }
    get left() {
        return this.value * 100;
    }
}
TuiLinearPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearPickerComponent, deps: [{ token: TuiPickerService }], target: i0.ɵɵFactoryTarget.Component });
TuiLinearPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiLinearPickerComponent, selector: "tui-linear-picker", inputs: { value: "value" }, outputs: { valueChange: "valueChange" }, providers: [TuiDestroyService, TuiPickerService], ngImport: i0, template: "<div\n    class=\"t-circle\"\n    [style.left.%]=\"left\"\n></div>\n", styles: [":host{position:relative;display:block;height:1rem;box-sizing:border-box}.t-circle{position:absolute;width:1rem;height:1rem;border-radius:100%;margin:0 -.5rem;box-shadow:inset 0 0 0 2px var(--tui-base-01),0 0 0 1px var(--tui-base-03)}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-linear-picker',
                    templateUrl: './linear-picker.template.html',
                    styleUrls: ['./linear-picker.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [TuiDestroyService, TuiPickerService],
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Observable, decorators: [{
                    type: Inject,
                    args: [TuiPickerService]
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class TuiColorPickerComponent {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
        this.colorChange = new EventEmitter();
        this.point = [0, 1];
        this.hue = 0;
        this.opacity = 1;
    }
    set color(color) {
        if (this.currentColor.every((item, index) => item === color[index]) &&
            color[3] === this.opacity) {
            return;
        }
        const [h, s, v] = tuiRgbToHsv(color[0], color[1], color[2]);
        this.opacity = color[3];
        this.hue = h / 360;
        this.point = [s, 1 - v / 255];
    }
    get currentColor() {
        return this.getCurrentColor(this.hue, this.point);
    }
    get base() {
        return `rgb(${tuiHsvToRgb(this.hue * 360, 1, 255)})`;
    }
    get gradient() {
        return this.sanitizer.bypassSecurityTrustStyle(`linear-gradient(to right, rgba(${this.currentColor.join(',')}, 0), rgb(${this.currentColor.join(',')}))`);
    }
    onPointChange(point) {
        this.point = point;
        this.updateColor();
    }
    onHueChange(hue) {
        this.hue = hue;
        this.updateColor();
    }
    onOpacityChange(opacity) {
        this.opacity = tuiRound(opacity, 2);
        this.updateColor();
    }
    getCurrentColor(hue, point) {
        return tuiHsvToRgb(hue * 360, point[0], (1 - point[1]) * 255);
    }
    updateColor() {
        this.colorChange.emit([...this.currentColor, this.opacity]);
    }
}
TuiColorPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorPickerComponent, deps: [{ token: DomSanitizer }], target: i0.ɵɵFactoryTarget.Component });
TuiColorPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiColorPickerComponent, selector: "tui-color-picker", inputs: { color: "color" }, outputs: { colorChange: "colorChange" }, ngImport: i0, template: "<tui-flat-picker\n    class=\"t-flat\"\n    [style.backgroundColor]=\"base\"\n    [value]=\"point\"\n    (valueChange)=\"onPointChange($event)\"\n></tui-flat-picker>\n<div class=\"t-wrapper t-wrapper_hue\">\n    <tui-linear-picker\n        class=\"t-hue\"\n        [value]=\"hue\"\n        (valueChange)=\"onHueChange($event)\"\n    ></tui-linear-picker>\n</div>\n<div\n    class=\"t-wrapper t-wrapper_opacity\"\n    [style.background]=\"gradient\"\n>\n    <tui-linear-picker\n        class=\"t-opacity\"\n        [value]=\"opacity\"\n        (valueChange)=\"onOpacityChange($event)\"\n    ></tui-linear-picker>\n</div>\n", styles: [":host{position:relative;z-index:0;display:block}.t-flat{height:14rem;background:linear-gradient(rgba(0,0,0,0),#000000),linear-gradient(to right,#ffffff,rgba(255,255,255,0));margin-bottom:.25rem;overflow:hidden}.t-wrapper{position:relative;margin:1rem 1.25rem;border-radius:.5rem}.t-wrapper:after{position:absolute;top:0;left:0;width:100%;height:100%;content:\"\";box-shadow:inset 0 0 0 1px #00000014;pointer-events:none;border-radius:inherit}.t-wrapper_hue{background:#ff0000}.t-wrapper_opacity:before{position:absolute;top:0;left:0;width:100%;height:100%;content:\"\";z-index:-1;background-image:linear-gradient(45deg,var(--tui-base-03) 25%,transparent 25%,transparent 75%,var(--tui-base-03) 75%,var(--tui-base-03)),linear-gradient(45deg,var(--tui-base-03) 25%,transparent 25%,transparent 75%,var(--tui-base-03) 75%,var(--tui-base-03));background-size:.375rem .375rem;background-position:0 0,.1875rem .1875rem;border-radius:inherit}.t-hue{background:linear-gradient(to right,#ff0000,#ffff00,#00ff00,#00ffff,#0000ff,#ff00ff,#ff0000);border-radius:inherit;margin:0 .5rem}.t-opacity{border-radius:inherit;margin:0 .5rem}\n"], components: [{ type: TuiFlatPickerComponent, selector: "tui-flat-picker", inputs: ["value"], outputs: ["valueChange"] }, { type: TuiLinearPickerComponent, selector: "tui-linear-picker", inputs: ["value"], outputs: ["valueChange"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    tuiPure
], TuiColorPickerComponent.prototype, "getCurrentColor", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-color-picker',
                    templateUrl: './color-picker.template.html',
                    styleUrls: ['./color-picker.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i3$1.DomSanitizer, decorators: [{
                    type: Inject,
                    args: [DomSanitizer]
                }] }]; }, propDecorators: { color: [{
                type: Input
            }], colorChange: [{
                type: Output
            }], getCurrentColor: [] } });

class TuiFlatPickerModule {
}
TuiFlatPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFlatPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiFlatPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFlatPickerModule, declarations: [TuiFlatPickerComponent], imports: [CommonModule], exports: [TuiFlatPickerComponent] });
TuiFlatPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFlatPickerModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFlatPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [TuiFlatPickerComponent],
                    exports: [TuiFlatPickerComponent],
                }]
        }] });

class TuiLinearPickerModule {
}
TuiLinearPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiLinearPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearPickerModule, declarations: [TuiLinearPickerComponent], imports: [CommonModule], exports: [TuiLinearPickerComponent] });
TuiLinearPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearPickerModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [TuiLinearPickerComponent],
                    exports: [TuiLinearPickerComponent],
                }]
        }] });

class TuiColorPickerModule {
}
TuiColorPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiColorPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorPickerModule, declarations: [TuiColorPickerComponent], imports: [CommonModule, TuiFlatPickerModule, TuiLinearPickerModule], exports: [TuiColorPickerComponent] });
TuiColorPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorPickerModule, imports: [[CommonModule, TuiFlatPickerModule, TuiLinearPickerModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TuiFlatPickerModule, TuiLinearPickerModule],
                    declarations: [TuiColorPickerComponent],
                    exports: [TuiColorPickerComponent],
                }]
        }] });

class TuiLinearMultiPickerComponent {
    constructor(point$) {
        this.value = [0, 1];
        this.valueChange = new EventEmitter();
        this.indexChange = new EventEmitter();
        this.index = NaN;
        point$.subscribe(([x]) => {
            this.onPicker(x);
        });
    }
    onMouseUp() {
        this.index = NaN;
    }
    onMouseDown(index) {
        this.updateIndex(index);
    }
    onClick(index) {
        if (this.value.length > 2) {
            this.updateValue(this.value.filter((_, i) => i !== index));
        }
    }
    onPicker(x) {
        if (Number.isNaN(this.index)) {
            this.updateIndex(this.value.length);
            this.value = [...this.value, 0];
        }
        if (this.value.some((item, index) => item === x && index !== this.index)) {
            return;
        }
        this.updateValue(this.value.map((item, index) => (index === this.index ? x : item)));
    }
    updateIndex(index) {
        this.index = index;
        this.indexChange.emit(index);
    }
    updateValue(value) {
        this.value = value;
        this.valueChange.emit(value);
    }
}
TuiLinearMultiPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearMultiPickerComponent, deps: [{ token: TuiPickerService }], target: i0.ɵɵFactoryTarget.Component });
TuiLinearMultiPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiLinearMultiPickerComponent, selector: "tui-linear-multi-picker", inputs: { value: "value" }, outputs: { valueChange: "valueChange", indexChange: "indexChange" }, host: { listeners: { "document:mouseup": "onMouseUp()" } }, providers: [TuiDestroyService, TuiPickerService], ngImport: i0, template: "<div\n    *ngFor=\"let item of value; let index = index\"\n    class=\"t-circle\"\n    [style.left.%]=\"item * 100\"\n    (mousedown)=\"onMouseDown(index)\"\n    (dblclick)=\"onClick(index)\"\n></div>\n", styles: [":host{position:relative;display:block;height:1rem;box-sizing:border-box}.t-circle{position:absolute;width:1rem;height:1rem;border-radius:100%;margin:0 -.5rem;box-shadow:inset 0 0 0 2px var(--tui-base-01),0 0 0 1px var(--tui-base-03)}\n"], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearMultiPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-linear-multi-picker',
                    templateUrl: './linear-multi-picker.template.html',
                    styleUrls: ['./linear-multi-picker.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [TuiDestroyService, TuiPickerService],
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Observable, decorators: [{
                    type: Inject,
                    args: [TuiPickerService]
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], indexChange: [{
                type: Output
            }], onMouseUp: [{
                type: HostListener,
                args: ['document:mouseup']
            }] } });

class TuiPaletteComponent {
    constructor() {
        this.colors = new Map();
        this.selectedColor = new EventEmitter();
        this.originalOrder = (_a, _b) => 0;
    }
}
TuiPaletteComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiPaletteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TuiPaletteComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiPaletteComponent, selector: "tui-palette", inputs: { colors: "colors" }, outputs: { selectedColor: "selectedColor" }, ngImport: i0, template: "<button\n    *ngFor=\"let color of colors | keyvalue : originalOrder\"\n    type=\"button\"\n    class=\"t-button\"\n    [style.background]=\"color.value\"\n    [tuiHint]=\"color.key\"\n    [tuiHintDescribe]=\"color.key\"\n    [id]=\"color.key\"\n    (click)=\"selectedColor.emit(color.value)\"\n></button>\n", styles: [":host{display:flex;flex-wrap:wrap;padding:.875rem}.t-button{-webkit-appearance:none;-moz-appearance:none;appearance:none;padding:0;border:0;background:none;font-size:inherit;line-height:inherit;width:1.5625rem;height:1.5625rem;box-sizing:border-box;margin:.375rem;border-radius:var(--tui-radius-m);border:1px solid rgba(0,0,0,.08)}\n"], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.TuiHintDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHint", "tuiHintContext", "tuiHintAppearance"] }, { type: i2.TuiHintDriverDirective, selector: "[tuiHint]" }, { type: i2.TuiHintHoverDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHintShowDelay", "tuiHintHideDelay"], exportAs: ["tuiHintHover"] }, { type: i2.TuiHintPositionDirective, selector: "[tuiHint]:not([tuiHintCustomPosition])", inputs: ["tuiHintDirection"] }, { type: i2.TuiHintDescribeDirective, selector: "[tuiHintDescribe]", inputs: ["tuiHintDescribe"] }], pipes: { "keyvalue": i1$1.KeyValuePipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiPaletteComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-palette',
                    templateUrl: './palette.template.html',
                    styleUrls: ['./palette.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { colors: [{
                type: Input
            }], selectedColor: [{
                type: Output
            }] } });

const EMPTY_STOP = [0, 0, 0, 0];
const DEFAULT_STEPS = [
    [0, [0, 0, 0, 1]],
    [1, [255, 255, 255, 1]],
];
const ICONS = {
    'to top right': 'tuiIconArrowUpRight',
    'to right': 'tuiIconArrowRight',
    'to bottom right': 'tuiIconArrowDownRight',
    'to bottom': 'tuiIconArrowDown',
    'to bottom left': 'tuiIconArrowDownLeft',
    'to left': 'tuiIconArrowLeft',
    'to top left': 'tuiIconArrowUpLeft',
    'to top': 'tuiIconArrowUp',
};
class TuiColorSelectorComponent {
    constructor(sanitizer, modes) {
        this.sanitizer = sanitizer;
        this.modes = modes;
        this.stops = new Map(DEFAULT_STEPS);
        this.currentStop = 0;
        this.direction = 'to bottom';
        this.colors = new Map();
        this.colorChange = new EventEmitter();
        this.color = [0, 0, 0, 1];
        this.currentMode = this.modes[0];
        this.buttons = [
            'to top right',
            'to right',
            'to bottom right',
            'to bottom',
            'to bottom left',
            'to left',
            'to top left',
            'to top',
        ];
    }
    set colorSetter(color) {
        this.parse(color);
    }
    get palette() {
        return this.filterPalette(this.colors, this.isGradient);
    }
    get stopsKeys() {
        return this.getStopsKeys(this.stops);
    }
    get currentColor() {
        return this.isGradient ? this.getStop(this.currentStop) : this.color;
    }
    get gradient() {
        return this.sanitizer.bypassSecurityTrustStyle(this.getGradient('to right'));
    }
    get isGradient() {
        return this.currentMode === this.modes[1];
    }
    getIcon(direction) {
        return ICONS[direction];
    }
    isModeActive(mode) {
        return this.currentMode === mode;
    }
    isDirectionActive(direction) {
        return this.direction === direction;
    }
    onPalettePick(color) {
        this.updateColor(color);
    }
    onDirectionChange(direction) {
        this.direction = direction;
        this.updateColor(this.getGradient(direction));
    }
    onModeSelect(mode, dropdown) {
        this.currentMode = mode;
        dropdown.open = false;
        this.updateColor(mode === this.modes[0]
            ? `rgba(${this.color.join(', ')})`
            : this.getGradient(this.direction));
    }
    onIndexChange(index) {
        this.currentStop = this.stopsKeys[index];
    }
    onColorChange(color) {
        if (!this.isGradient) {
            this.updateColor(`rgba(${color.join(', ')})`);
            return;
        }
        this.stops.set(this.currentStop, color);
        this.updateColor(this.getGradient(this.direction));
    }
    onStopsChange(stopsKeys) {
        const removed = this.stopsKeys.find(item => !stopsKeys.includes(item));
        const added = stopsKeys.find(item => !this.stopsKeys.includes(item));
        if (removed === undefined && added !== undefined) {
            this.addStop(added);
        }
        if (removed !== undefined && added === undefined) {
            this.removeStop(removed);
        }
        if (removed !== undefined && added !== undefined) {
            this.replaceStop(removed, added);
        }
        this.updateColor(this.getGradient(this.direction));
    }
    getStopsKeys(stops) {
        return Array.from(stops.keys());
    }
    filterPalette(colors, isGradient) {
        const map = new Map(colors);
        map.forEach((value, key) => {
            if ((value.startsWith('linear-gradient') && !isGradient) ||
                (!value.startsWith('linear-gradient') && isGradient)) {
                map.delete(key);
            }
        });
        return map;
    }
    updateColor(color) {
        this.colorChange.emit(color);
    }
    getGradient(direction) {
        return `linear-gradient(${direction}, ${[...this.stopsKeys]
            .sort(tuiDefaultSort)
            .map(key => `rgba(${this.getStop(key).join(', ')}) ${key * 100}%`)
            .join(', ')})`;
    }
    getStop(stop) {
        return this.stops.get(stop) || EMPTY_STOP;
    }
    addStop(stop) {
        const closest = this.stopsKeys.reduce((prev, curr) => (Math.abs(curr - stop) < Math.abs(prev - stop) ? curr : prev), this.stopsKeys[0]);
        this.stops.set(stop, this.getStop(closest));
        this.stops = new Map(this.stops);
        this.currentStop = stop;
    }
    removeStop(stop) {
        this.stops.delete(stop);
        this.stops = new Map(this.stops);
        this.currentStop = this.stopsKeys[0];
    }
    replaceStop(removed, added) {
        const value = this.getStop(removed);
        this.currentStop = added;
        this.stops = new Map(this.stopsKeys.map(key => key === removed ? [added, value] : [key, this.getStop(key)]));
    }
    parse(color) {
        if (color.startsWith('linear-gradient')) {
            this.parseGradient(color);
        }
        else {
            this.parseColor(color);
        }
    }
    parseGradient(color) {
        if (color === this.getGradient(this.direction)) {
            return;
        }
        const gradient = tuiParseGradient(tuiGetGradientData(color));
        this.currentMode = this.modes[1];
        this.direction = gradient.side;
        this.currentStop = 0;
        this.stops = new Map(gradient.stops.length
            ? gradient.stops.map(({ color, position }) => [
                parseFloat(position) / 100,
                tuiParseColor(color),
            ])
            : DEFAULT_STEPS);
    }
    parseColor(color) {
        this.currentMode = this.modes[0];
        this.currentStop = 0;
        this.color = tuiParseColor(color);
    }
}
TuiColorSelectorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorSelectorComponent, deps: [{ token: DomSanitizer }, { token: TUI_EDITOR_COLOR_SELECTOR_MODE_NAMES }], target: i0.ɵɵFactoryTarget.Component });
TuiColorSelectorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiColorSelectorComponent, selector: "tui-color-selector", inputs: { colors: "colors", colorSetter: ["color", "colorSetter"] }, outputs: { colorChange: "colorChange" }, ngImport: i0, template: "<tui-hosted-dropdown\n    #dropdown\n    class=\"t-select\"\n    [content]=\"menu\"\n>\n    <button\n        tuiButton\n        appearance=\"\"\n        size=\"s\"\n        type=\"button\"\n        [iconRight]=\"arrow\"\n    >\n        {{ currentMode }}\n    </button>\n    <ng-template #arrow>\n        <tui-svg\n            src=\"tuiIconChevronDown\"\n            class=\"t-arrow\"\n            [class.t-arrow_rotated]=\"dropdown.open\"\n        ></tui-svg>\n    </ng-template>\n    <ng-template #menu>\n        <tui-data-list\n            role=\"menu\"\n            size=\"s\"\n            class=\"t-menu\"\n        >\n            <button\n                *ngFor=\"let mode of modes\"\n                tuiOption\n                role=\"menuitemradio\"\n                [attr.aria-checked]=\"isModeActive(mode)\"\n                (keydown.enter.prevent)=\"onModeSelect(mode, dropdown)\"\n                (keydown.space.prevent)=\"onModeSelect(mode, dropdown)\"\n                (click)=\"onModeSelect(mode, dropdown)\"\n            >\n                {{ mode }}\n                <tui-svg\n                    *ngIf=\"isModeActive(mode)\"\n                    src=\"tuiIconCheck\"\n                    class=\"t-checkmark\"\n                ></tui-svg>\n            </button>\n        </tui-data-list>\n    </ng-template>\n</tui-hosted-dropdown>\n<ng-container *ngIf=\"isGradient\">\n    <hr class=\"t-hr\" />\n    <div\n        class=\"t-wrapper\"\n        [style.background]=\"gradient\"\n    >\n        <tui-linear-multi-picker\n            class=\"t-gradient\"\n            [value]=\"stopsKeys\"\n            (valueChange)=\"onStopsChange($event)\"\n            (indexChange)=\"onIndexChange($event)\"\n        ></tui-linear-multi-picker>\n    </div>\n    <div class=\"t-buttons\">\n        <!-- TODO: Change to `tuiHintDescribe` when figure tuiDriver order issue -->\n        <button\n            *ngFor=\"let button of buttons\"\n            tuiIconButton\n            type=\"button\"\n            size=\"xs\"\n            appearance=\"\"\n            tuiHintDescribe\n            class=\"t-direction\"\n            [class.t-direction_active]=\"isDirectionActive(button)\"\n            [icon]=\"getIcon(button)\"\n            [tuiHint]=\"button\"\n            (click)=\"onDirectionChange(button)\"\n        ></button>\n    </div>\n</ng-container>\n<tui-color-picker\n    [color]=\"currentColor\"\n    (colorChange)=\"onColorChange($event)\"\n></tui-color-picker>\n<tui-color-edit\n    *ngIf=\"!isGradient\"\n    tuiGroup\n    class=\"t-edit\"\n    [color]=\"color\"\n    (colorChange)=\"onColorChange($event)\"\n></tui-color-edit>\n<tui-palette\n    *ngIf=\"palette.size\"\n    class=\"t-palette\"\n    [colors]=\"palette\"\n    (selectedColor)=\"onPalettePick($event)\"\n></tui-palette>\n", styles: [":host{position:relative;z-index:0;display:block;width:22.6rem}.t-wrapper{position:relative;margin:1.25rem;border-radius:.5rem}.t-wrapper:after{position:absolute;top:0;left:0;width:100%;height:100%;content:\"\";box-shadow:inset 0 0 0 1px #00000014;pointer-events:none;border-radius:inherit}.t-wrapper:before{position:absolute;top:0;left:0;width:100%;height:100%;content:\"\";z-index:-1;background-image:linear-gradient(45deg,var(--tui-base-03) 25%,transparent 25%,transparent 75%,var(--tui-base-03) 75%,var(--tui-base-03)),linear-gradient(45deg,var(--tui-base-03) 25%,transparent 25%,transparent 75%,var(--tui-base-03) 75%,var(--tui-base-03));background-size:.375rem .375rem;background-position:0 0,.1875rem .1875rem;border-radius:inherit}.t-hr{height:1px;margin:0 0 0 -1px;border:none;background:var(--tui-base-03)}.t-gradient{margin:0 .5rem;border-radius:inherit}.t-select{margin:.75rem .5rem}.t-arrow{transition-property:transform;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out}.t-arrow_rotated{transform:rotate(180deg)}.t-menu{width:11.25rem}.t-checkmark{margin-left:auto;width:1rem;height:1rem}.t-buttons{display:flex;padding:0 .75rem 1.25rem}.t-direction{color:var(--tui-text-02);margin:0 .375rem}.t-direction:hover,.t-direction_active{color:var(--tui-text-01);background:var(--tui-secondary-hover)}.t-edit{margin:1.25rem}.t-palette{box-sizing:border-box;box-shadow:inset 0 1px var(--tui-base-03)}\n"], components: [{ type: i2.TuiHostedDropdownComponent, selector: "tui-hosted-dropdown", inputs: ["content", "sided", "canOpen", "open"], outputs: ["openChange", "focusedChange"] }, { type: i2.TuiButtonComponent, selector: "button[tuiButton], button[tuiIconButton], a[tuiButton], a[tuiIconButton]", inputs: ["appearance", "disabled", "icon", "iconRight", "shape", "showLoader", "size"] }, { type: i2.TuiSvgComponent, selector: "tui-svg", inputs: ["src"] }, { type: i2.TuiDataListComponent, selector: "tui-data-list", inputs: ["role", "emptyContent", "size"] }, { type: i2.TuiOptionComponent, selector: "button[tuiOption], a[tuiOption]", inputs: ["size", "role", "disabled", "value"] }, { type: TuiLinearMultiPickerComponent, selector: "tui-linear-multi-picker", inputs: ["value"], outputs: ["valueChange", "indexChange"] }, { type: TuiColorPickerComponent, selector: "tui-color-picker", inputs: ["color"], outputs: ["colorChange"] }, { type: TuiColorEditComponent, selector: "tui-color-edit", inputs: ["color"], outputs: ["colorChange"] }, { type: TuiPaletteComponent, selector: "tui-palette", inputs: ["colors"], outputs: ["selectedColor"] }], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.TuiHintDescribeDirective, selector: "[tuiHintDescribe]", inputs: ["tuiHintDescribe"] }, { type: i2.TuiHintDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHint", "tuiHintContext", "tuiHintAppearance"] }, { type: i2.TuiHintDriverDirective, selector: "[tuiHint]" }, { type: i2.TuiHintHoverDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHintShowDelay", "tuiHintHideDelay"], exportAs: ["tuiHintHover"] }, { type: i2.TuiHintPositionDirective, selector: "[tuiHint]:not([tuiHintCustomPosition])", inputs: ["tuiHintDirection"] }, { type: i2.TuiGroupDirective, selector: "[tuiGroup]:not(ng-container)", inputs: ["orientation", "adaptive", "collapsed", "rounded", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    tuiPure
], TuiColorSelectorComponent.prototype, "getStopsKeys", null);
__decorate([
    tuiPure
], TuiColorSelectorComponent.prototype, "filterPalette", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorSelectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-color-selector',
                    templateUrl: './color-selector.template.html',
                    styleUrls: ['./color-selector.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i3$1.DomSanitizer, decorators: [{
                    type: Inject,
                    args: [DomSanitizer]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_COLOR_SELECTOR_MODE_NAMES]
                }] }]; }, propDecorators: { colors: [{
                type: Input
            }], colorSetter: [{
                type: Input,
                args: ['color']
            }], colorChange: [{
                type: Output
            }], getStopsKeys: [], filterPalette: [] } });

class TuiLinearMultiPickerModule {
}
TuiLinearMultiPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearMultiPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiLinearMultiPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearMultiPickerModule, declarations: [TuiLinearMultiPickerComponent], imports: [CommonModule], exports: [TuiLinearMultiPickerComponent] });
TuiLinearMultiPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearMultiPickerModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiLinearMultiPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [TuiLinearMultiPickerComponent],
                    exports: [TuiLinearMultiPickerComponent],
                }]
        }] });

class TuiPaletteModule {
}
TuiPaletteModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiPaletteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiPaletteModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiPaletteModule, declarations: [TuiPaletteComponent], imports: [CommonModule, TuiHintModule], exports: [TuiPaletteComponent] });
TuiPaletteModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiPaletteModule, imports: [[CommonModule, TuiHintModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiPaletteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TuiHintModule],
                    declarations: [TuiPaletteComponent],
                    exports: [TuiPaletteComponent],
                }]
        }] });

class TuiColorSelectorModule {
}
TuiColorSelectorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorSelectorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiColorSelectorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorSelectorModule, declarations: [TuiColorSelectorComponent], imports: [CommonModule,
        TuiButtonModule,
        TuiHostedDropdownModule,
        TuiColorPickerModule,
        TuiLinearMultiPickerModule,
        TuiHintModule,
        TuiSvgModule,
        TuiDataListModule,
        TuiActiveZoneModule,
        TuiColorEditModule,
        TuiGroupModule,
        TuiPaletteModule], exports: [TuiColorSelectorComponent] });
TuiColorSelectorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorSelectorModule, imports: [[
            CommonModule,
            TuiButtonModule,
            TuiHostedDropdownModule,
            TuiColorPickerModule,
            TuiLinearMultiPickerModule,
            TuiHintModule,
            TuiSvgModule,
            TuiDataListModule,
            TuiActiveZoneModule,
            TuiColorEditModule,
            TuiGroupModule,
            TuiPaletteModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiColorSelectorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TuiButtonModule,
                        TuiHostedDropdownModule,
                        TuiColorPickerModule,
                        TuiLinearMultiPickerModule,
                        TuiHintModule,
                        TuiSvgModule,
                        TuiDataListModule,
                        TuiActiveZoneModule,
                        TuiColorEditModule,
                        TuiGroupModule,
                        TuiPaletteModule,
                    ],
                    declarations: [TuiColorSelectorComponent],
                    exports: [TuiColorSelectorComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiColorEditComponent, TuiColorEditModule, TuiColorPickerComponent, TuiColorPickerModule, TuiColorSelectorComponent, TuiColorSelectorModule, TuiFlatPickerComponent, TuiFlatPickerModule, TuiLinearMultiPickerComponent, TuiLinearMultiPickerModule, TuiLinearPickerComponent, TuiLinearPickerModule, TuiPaletteComponent, TuiPaletteModule };
